/**
 * @fileoverview Global state reducer for the application
 * 
 * This file contains the main reducer function that handles all state updates
 * for the global application context. It processes dispatched actions and
 * returns the updated state according to the action type.
 * 
 * The reducer follows the standard pattern of checking action types and
 * returning a new state object without mutating the original state.
 */

import { ActionType } from './types';
import { initialState } from './index';

/**
 * Main reducer function for the global application state
 * 
 * This function processes all dispatched actions and returns an updated state
 * based on the action type. It implements a pure function pattern where:
 * - The original state is never mutated
 * - A new state object is always returned
 * - State updates are predictable and based solely on the current state and action
 * 
 * @param {any} state - The current application state
 * @param {ActionType} action - The dispatched action with type and optional payload
 * @returns {any} The new application state after applying the action
 */
const Reducer = (state: any, action: ActionType): any => {
  switch (action.type) {
    case "SET_LEADER_DATA":
      // Update leader profile data in the state
      return {
        ...state,
        leaderData: action.payload,
      };
    case "SET_CLIENT_DATA":
      // Update client configuration data in the state
      return {
        ...state,
        clientData: action.payload,
      };
    case "SET_LOOKUP_DATA":
      // Update lookup reference data for dropdowns and selects
      return {
        ...state,
        lookUpData: action.payload,
      };
    case "SET_SHOW_LOGOUT_MODAL":
      // Toggle visibility of the logout confirmation modal
      return {
        ...state,
        showLogoutModal: action.payload,
      };
    case "SET_LEFT_PANEL_IMG":
      return {
        ...state,
        leftPanelImg: action.payload,
      };
    case "SET_HEADER_LOGO":
      return {
        ...state,
        headerLogo: action.payload,
      };
    case "SET_HEADER_IMG":
      return {
        ...state,
        headerImg: action.payload,
      };
    case "SET_PRIMARY_COLOR":
      return {
        ...state,
        primaryColor: action.payload,
      };
    case "SET_IMMERSIVE_ASSESSMENT_URL":
      return {
        ...state,
        immersiveAssessmentData: action.payload,
      };
    case "SET_IN_HOUSE_ASSESSMENT_DATA":
      return {
        ...state,
        inHouseAssessmentData: action.payload,
      };
    case "SET_DEMOGRAPHIC_ACKNOWLEDGED":
      // Update demographic acknowledgement status
      return {
        ...state,
        demographicAcknowledged: action.payload,
      };
    case "PURGE_STATE":
      // Reset the entire state to initial values (used during logout)
      return initialState;
    default:
      // Return unchanged state for unknown action types
      return state;
  }
};

/**
 * Export the Reducer function as the default export
 * This reducer is used by the GlobalStore component in index.tsx
 * with the useReducer hook to manage application state
 */
export default Reducer;
